import { useState, useEffect, useContext } from 'react';
import toastError from '../../toasts/toastError';
import { toast } from 'react-toastify';

import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';

import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  useTheme,
} from '@mui/material';

import QueueSelect from '../QueueSelect';
import { Can } from '../Can';

import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { getStyled } from './styles';

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Required'),
  password: Yup.string().min(5, 'Too Short!').max(50, 'Too Long!'),
  email: Yup.string().email('Invalid email').required('Required'),
});

const ModalUsers = ({ open, onClose, userId, companyId }) => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const initialState = {
    name: '',
    email: '',
    password: '',
    profile: 'user',
  };

  const { user: loggedInUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      if (open) {
        try {
          const { data } = await api.get(`/users/${userId}`);
          setUser((prevState) => {
            return { ...prevState, ...data };
          });
          const userQueueIds = data.queues?.map((queue) => queue.id);
          setSelectedQueueIds(userQueueIds);
        } catch (err) {
          toastError(err);
        }
      }
    };

    fetchUser();
  }, [userId, open]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
  };

  const handleSaveUser = async (values) => {
    const userData = { ...values, companyId, queueIds: selectedQueueIds };
    try {
      if (userId) {
        await api.put(`/users/${userId}`, userData);
      } else {
        await api.post('/users', userData);
      }
      toastSuccess(i18n.t('userModal.success'));
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <div style={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='xs'
        fullWidth
        scroll='paper'
      >
        <DialogTitle id='form-dialog-title'>
          {userId
            ? `${i18n.t('userModal.title.edit')}`
            : `${i18n.t('userModal.title.add')}`}
        </DialogTitle>
        <Formik
          initialValues={user}
          enableReinitialize={true}
          validationSchema={UserSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveUser(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <div style={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t('userModal.form.name')}
                    autoFocus
                    name='name'
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant='outlined'
                    margin='dense'
                    fullWidth
                  />
                  <Field
                    as={TextField}
                    label={i18n.t('userModal.form.password')}
                    type='password'
                    name='password'
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    variant='outlined'
                    margin='dense'
                    fullWidth
                  />
                </div>
                <div style={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t('userModal.form.email')}
                    name='email'
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    variant='outlined'
                    margin='dense'
                    fullWidth
                  />
                  <FormControl
                    variant='outlined'
                    sx={classes.formControl}
                    margin='dense'
                  >
                    <Can
                      role={loggedInUser.profile}
                      perform='user-modal:editProfile'
                      yes={() => (
                        <>
                          <InputLabel id='profile-selection-input-label'>
                            {i18n.t('userModal.form.profile')}
                          </InputLabel>

                          <Field
                            as={Select}
                            label={i18n.t('userModal.form.profile')}
                            name='profile'
                            labelId='profile-selection-label'
                            id='profile-selection'
                            required
                          >
                            <MenuItem value='admin'>Admin</MenuItem>
                            <MenuItem value='user'>User</MenuItem>
                          </Field>
                        </>
                      )}
                    />
                  </FormControl>
                </div>
                <Can
                  role={loggedInUser.profile}
                  perform='user-modal:editQueues'
                  yes={() => (
                    <QueueSelect
                      selectedQueueIds={selectedQueueIds}
                      onChange={(values) => setSelectedQueueIds(values)}
                    />
                  )}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color='secondary'
                  disabled={isSubmitting}
                  variant='outlined'
                >
                  {i18n.t('userModal.buttons.cancel')}
                </Button>
                <Button
                  type='submit'
                  color='primary'
                  disabled={isSubmitting}
                  variant='outlined'
                  sx={theme.custom.confirmButton}
                >
                  {userId
                    ? `${i18n.t('userModal.buttons.okEdit')}`
                    : `${i18n.t('userModal.buttons.okAdd')}`}
                  {isSubmitting && (
                    <CircularProgress size={24} sx={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ModalUsers;
